import { useEffect } from "react";
import PropTypes from 'prop-types'; // Recommended for type checking (though not directly used in a hook)

/**
 * @typedef {Object} RefObject
 * @property {HTMLElement | null} current - The current DOM element reference.
 */

/**
 * Custom React hook that calls a handler function when a click event
 * occurs outside of the referenced DOM element.
 *
 * This is commonly used for implementing close behavior on modals,
 * dropdowns, and popovers.
 *
 * @param {RefObject} ref - A React ref object pointing to the element to watch.
 * @param {() => void} onClose - The callback function to execute when a click outside occurs.
 */
export function useClickOutside(ref, onClose) {
    useEffect(() => {
        /**
         * Handler function that checks if the click target is outside the referenced element.
         * @param {MouseEvent} event
         */
        function handleClick(event) {
            // If the ref exists AND the click target is NOT within the referenced element
            if (ref.current && !ref.current.contains(event.target)) {
                onClose();
            }
        }

        // Attach the listener when the component mounts
        document.addEventListener("mousedown", handleClick);

        // Clean up the listener when the component unmounts or dependencies change
        return () => document.removeEventListener("mousedown", handleClick);
    }, [ref, onClose]); // Dependencies ensure the latest ref and onClose function are used
}
